import React from "react";

import { OrderProps } from "store/modules/Orders/types.store";

import DriverEditView from "./DriverEditView";
import DriverReadView from "./DriverReadView";
import {
  getOrderDriverPermissions,
  hasOrderPreAssignedDriver,
  isStoreOrderMapType,
  orderHasAssignedDriver,
  resolveCoverageAreaMapType,
  resolveOrderDriverDisplay,
} from "./getOrderDriverPermissions";
import { Wrapper } from "./styles";
import { useOrderDriverSelection } from "./useOrderDriverSelection";

interface OrderDriverSectionProps {
  data: OrderProps;
  currentTab?: string;
}

function OrderDriverSection({
  data,
  currentTab,
}: OrderDriverSectionProps): JSX.Element {
  const dealerId = data?.dealer?.id ?? "";
  const orderId = data?.id ?? "";
  const coverageAreaId = data?.coverageArea?.id;

  const {
    isSelecting,
    loadingDrivers,
    hasDriverInsights,
    driverOptions,
    selectedDriver,
    setSelectedDriver,
    openSelection,
    closeSelection,
    applySelection,
  } = useOrderDriverSelection({ dealerId, orderId, coverageAreaId });

  const hasOrderDriver = orderHasAssignedDriver(data?.driver);
  const mapType = resolveCoverageAreaMapType({
    placedOrigin: data?.placedOrigin,
  });
  const hasPreAssignedDriver =
    !hasOrderDriver &&
    hasOrderPreAssignedDriver({
      placedOrigin: data?.placedOrigin,
      coverageArea: data?.coverageArea,
    });
  const driverDisplay = resolveOrderDriverDisplay({
    orderDriver: data?.driver,
    placedOrigin: data?.placedOrigin,
    coverageArea: data?.coverageArea,
  });
  const driverPermissions = getOrderDriverPermissions({
    mapType,
    currentTab,
    hasOrderDriver,
    hasPreAssignedDriver,
  });

  if (isSelecting) {
    return (
      <Wrapper>
        <DriverEditView
          loading={loadingDrivers}
          hasDriverInsights={hasDriverInsights}
          options={driverOptions}
          value={selectedDriver}
          onChange={setSelectedDriver}
          onCancel={closeSelection}
          onApply={applySelection}
        />
      </Wrapper>
    );
  }

  const shouldHideBottomDivider =
    isStoreOrderMapType(mapType) && !driverDisplay.hasVisibleDriver;

  return (
    <Wrapper $disableBorder={shouldHideBottomDivider}>
      <DriverReadView
        driverName={driverDisplay.name}
        driverSurname={driverDisplay.surname}
        showDriverIdentity={driverDisplay.hasVisibleDriver}
        canSelectDriver={driverPermissions.canSelectDriver}
        canChangeDriver={driverPermissions.canChangeDriver}
        onSelect={openSelection}
      />
    </Wrapper>
  );
}

export default OrderDriverSection;
